/**
 * @file useLicenseTableOptions.ts
 * @desc Hook for reusable license table options (expand/collapse, etc.)
 */

import { useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import TuneIcon from "@mui/icons-material/Tune";
import { TFunction } from "i18next";

type TableOption = {
  option: string;
  label: string;
  icon: any;
  action: () => void;
};

type UseLicenseTableOptionsParams = {
  apiRef: ReturnType<typeof useGridApiRef>;
  t: TFunction;
};

export const useLicenseTableOptions = ({
  apiRef,
  t,
}: UseLicenseTableOptionsParams): {
  tableOptions: TableOption[];
  expandedRows: Set<string>;
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  allExpanded: boolean;
  setAllExpanded: React.Dispatch<React.SetStateAction<boolean>>;
} => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState<boolean>(false);

  const tableOptions: TableOption[] = [
    {
      option: "openCells",
      label: t("licenses.openCells"),
      icon: TuneIcon,
      action: () => {
        const allGroupIds = [
          ...new Set(
            apiRef.current
              .getAllRowIds()
              .map((id) => apiRef.current.getRowNode(id)?.parent)
              .filter((parent): parent is string => typeof parent === "string"),
          ),
        ];

        if (allExpanded) {
          allGroupIds.forEach((id) =>
            apiRef.current.setRowChildrenExpansion(id, false),
          );
          setExpandedRows(new Set());
        } else {
          allGroupIds.forEach((id) =>
            apiRef.current.setRowChildrenExpansion(id, true),
          );
          setExpandedRows(new Set(allGroupIds));
        }
        setAllExpanded(!allExpanded);
      },
    },
  ];

  return {
    tableOptions,
    expandedRows,
    setExpandedRows,
    allExpanded,
    setAllExpanded,
  };
};
